/**
 * useElementPosition — Track the bounding rectangle of an element by data-help-id
 *
 * Finds the target element via `querySelector('[data-help-id="..."]')` and
 * returns its bounding client rect. Tracks position changes on:
 * - Window scroll (requestAnimationFrame for performance)
 * - Window resize (debounced 100ms)
 * - DOM mutations (MutationObserver for dynamically rendered elements)
 *
 * Returns null if the element is not found or helpId is null.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface ElementRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Find and track the position of a DOM element identified by its `data-help-id`.
 *
 * @param helpId - The help ID to look up, or null to disable tracking
 * @returns The bounding rect of the element, or null if not found
 */
export function useElementPosition(helpId: string | null): ElementRect | null {
  const [rect, setRect] = useState<ElementRect | null>(null);
  const rafIdRef = useRef<number>(0);
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updatePosition = useCallback(() => {
    if (!helpId) {
      setRect(null);
      return;
    }

    let el: Element | null = null;
    try {
      el = document.querySelector(`[data-help-id="${CSS.escape(helpId)}"]`);
    } catch {
      // Invalid selector — fail closed
    }
    if (!el) {
      setRect(null);
      return;
    }

    const domRect = el.getBoundingClientRect();
    setRect((prev) => {
      // Only update state if values actually changed to avoid re-renders
      if (
        prev?.x === domRect.x &&
        prev.y === domRect.y &&
        prev.width === domRect.width &&
        prev.height === domRect.height
      ) {
        return prev;
      }
      return {
        x: domRect.x,
        y: domRect.y,
        width: domRect.width,
        height: domRect.height,
      };
    });
  }, [helpId]);

  useEffect(() => {
    if (!helpId) {
      setRect(null);
      return;
    }

    // Initial position
    updatePosition();

    // Scroll handler — use requestAnimationFrame for smooth tracking
    const onScroll = () => {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(updatePosition);
    };

    // Resize handler — debounced 100ms
    const onResize = () => {
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
      resizeTimerRef.current = setTimeout(updatePosition, 100);
    };

    // MutationObserver — for dynamically rendered elements
    const observer = new MutationObserver(() => {
      // Re-check position when DOM changes (element may appear/disappear)
      updatePosition();
    });

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-help-id", "class", "style"],
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(rafIdRef.current);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
      observer.disconnect();
    };
  }, [helpId, updatePosition]);

  return rect;
}
